import axios from 'axios'
import { get } from 'svelte/store'
import { server_path, detected_fonts, system_fonts, font_upload_progress, user_uploaded_fonts, user_uploaded_fonts_loading_state } from '../stores'
import { getFontStyleAsCssValue } from '../modules/fontDetection'
import opentype from 'opentype.js'
import { map, merge, isEmpty } from 'lodash'

let processedFontObjects = [];
let uploadedFontFiles = [];
let uploadedFontFilePaths = [];
const BATCH_SIZE = 5; // Process 5 fonts at a time
let loadedFontsFaces = new Map();

// -------------------------------------
// API endpoint functions for font files
// -------------------------------------

// Load uploaded fonts data
export async function loadUploadedFonts() {
  console.log('Font Files - loading uploaded fonts')
  
  try {
    // Fetch uploaded fonts from server
    const response = await axios.get(get(server_path) + 'fonts/get.php', { withCredentials: true });
    if (response.data && response.data.success) {
      console.log('Font Files - fetched uploaded fonts successfully')

      // set to store
      user_uploaded_fonts.set(response.data.data || []);
      
      // set loading state to success
      user_uploaded_fonts_loading_state.setSuccess();
      
      // map to fonts
      mapUploadedFontsDataToFonts(response.data.data);

    } else {
      user_uploaded_fonts_loading_state.setError(response.data?.error || 'Unknown error');
    }
  } catch (error) {
    user_uploaded_fonts_loading_state.setError('Network error');
    console.error('Error getting uploaded fonts:', error);
  }
}

// Map uploaded fonts to detected fonts using metadata
export function mapUploadedFontsDataToFonts(uploadedFonts = null) {
  try {
    // Use provided data or fall back to store data
    const fontsToMap = uploadedFonts || get(user_uploaded_fonts);
    
    if (!fontsToMap || fontsToMap.length === 0) {
      console.log('Font Files - no uploaded fonts to map');
      return;
    }

    const detectedFonts = [...get(detected_fonts)];
    let placeholderFontsToLog = [];

    fontsToMap.forEach(uploadedFont => {
      // Skip if no metadata
      if (!uploadedFont.metadata || !uploadedFont.metadata.postscript_name) {
        console.warn('Font Files - skipping font without metadata:', uploadedFont.file_name);
        return;
      }

      const postscriptName = uploadedFont.metadata.postscript_name;
      const displayName = uploadedFont.metadata.display_name || postscriptName;

      // Find existing font in detected fonts
      const fontIndex = detectedFonts.findIndex(f => f.postscript_name === postscriptName);

      if (fontIndex !== -1) {
        // Update existing font object
        detectedFonts[fontIndex] = {
          ...detectedFonts[fontIndex],
          file_name: uploadedFont.file_name,
          file_url: uploadedFont.file_url,
          uploaded_at: uploadedFont.metadata?.uploaded_at,
          uploaded: true
        };
      } else {
        // Create new placeholder font object
        const newFont = {
          id: detectedFonts.length > 0 ? Math.max(...detectedFonts.map(f => f.id)) + 1 : 0,
          postscript_name: postscriptName,
          family_name: displayName,
          display_name: displayName,
          file_name: uploadedFont.file_name,
          file_url: uploadedFont.file_url,
          uploaded_at: uploadedFont.metadata?.uploaded_at,
          uploaded: true,
          source: 'uploaded',
          system: false,
          user: true
        };

        detectedFonts.push(newFont);
        placeholderFontsToLog.push({
          id: newFont.id,
          postscript_name: postscriptName
        });
      }
    });

    // Update detected fonts store
    detected_fonts.set(detectedFonts);

    // Log placeholder fonts if any were created
    if (placeholderFontsToLog.length > 0) {
      console.log('Font Files - added placeholder fonts to detected fonts:', placeholderFontsToLog);
    }

    console.log('Font Files - successfully mapped uploaded fonts to detected fonts');
  } catch (error) {
    console.error('Font Files - error in mapUploadedFontsDataToFonts:', error);
    throw error;
  }
}

// Delete specified font files from the server
export async function deleteFontFiles(file_names) {
  console.log('Font Files - deleting fonts')
  
  let form_data = new FormData()
  file_names.forEach(file_name => {
    form_data.append('file_names[]', file_name)
  })
  
  let response = await axios.post(get(server_path) + 'fonts/delete.php', form_data, { withCredentials: true })
  if (response.data.success) {
    console.log('Font Files - deleted successfully')
  } else {
    console.log('Font Files - delete failed', response.data.delete_errors)
  }
  return response
}

// ----------------------------------------------------
// MAIN function to load, process and merge a font file
// ----------------------------------------------------
export async function loadAndProcessUploadedFontFile(fileName, fileUrl, postscriptName, fontId = null) {
  // Return false if fileName or fileUrl is missing
  if (!fileName || !fileUrl) {
    console.log("Font Files - missing fileName or fileUrl, cannot load font");
    detected_fonts.update(fonts => 
      fonts.map(f => (f.postscript_name === postscriptName ? { ...f, font_file_loading: false, font_file_loaded: false, failed_to_load_font_file: true } : f))
    );
    return false;
  }

  try {
    const file = await readFontFileData(fileName, fileUrl);
    let processedFont = await processFontFile(file, 'uploaded', fontId);

    if (processedFont) {

      // set loading and loaded to false on processedFont
      processedFont.font_file_loading = false;
      processedFont.font_file_loaded = true;

      // Update detected_fonts store with processed data
      addOrMergeFontObjects([processedFont]);

      return true;
    }
    return false;
  } catch (error) {
    console.error("Failed to load and process font file:", error);
    detected_fonts.update(fonts => 
      fonts.map(f => (f.postscript_name === postscriptName ? { ...f, font_file_loading: false, font_file_loaded: false, failed_to_load_font_file: true } : f))
    );
    return false;
  }
}

export const processFontFile = async (file, source, fontId = null) => {
  // file and source are required
  if (!file || !source) {
    console.error('Font Files - missing file or source, cannot process font');
    return null;
  }

  return new Promise(async (resolve) => {
    try {
      console.log(`Processing font file: ${file.name}, size: ${file.size} bytes`);
      const fileContents = await readFontFileContents(file);
      
      try {
        const parsedFont = opentype.parse(fileContents);
        const metadata = extractFontMetadata(parsedFont);
        
        // Use provided fontId or auto-assign incremental ID
        let finalFontId;
        if (fontId !== null) {
          finalFontId = fontId;
        } else {
          const currentFonts = get(detected_fonts);
          const maxId = currentFonts.length > 0 ? Math.max(...currentFonts.map(f => f.id)) : -1;
          finalFontId = maxId + 1;
        }
        
        const fontObject = createFontObject(
          metadata,
          file.name,
          finalFontId,
          source,
        );

        // Try to inject font face using original data
        try {
          await injectFontFace(fontObject.postscript_name, fileContents);
        } catch (fontFaceError) {
          console.warn(`Initial font loading failed for ${fontObject.postscript_name}, attempting repair`);
          
          // Try to repair and load the font
          await repairAndLoadFont(parsedFont, fontObject.postscript_name);
        }

        resolve(fontObject);
      } catch (parseError) {
        console.error(`Failed to parse font file ${file.name}: ${parseError.message}`);
        resolve(null);
      }
    } catch (error) {
      console.error(`Failed to process font file ${file.name}: ${error.message}`);
      resolve(null);
    }
  });
};

/**
 * Attempts to repair a font by regenerating it from parsed data
 * and then loading it into the document
 */
const repairAndLoadFont = async (parsedFont, postscriptName) => {
  try {
    console.log(`Attempting to repair font: ${postscriptName}`);
    
    // Use the parsed font to generate a new, clean TTF ArrayBuffer
    const repairedFontData = parsedFont.toArrayBuffer();
    
    // Try to load the repaired font
    await injectFontFace(postscriptName, repairedFontData, 'font/ttf');
    console.log(`Successfully loaded repaired font: ${postscriptName}`);
    return true;
  } catch (repairError) {
    console.error(`Font repair failed for ${postscriptName}:`, repairError);
    throw repairError;
  }
};

// LOADS a font face into the document
const injectFontFace = async (postscriptName, fontData, preferredType = null) => {
  // Remove existing font if it exists
  if (loadedFontsFaces.has(postscriptName)) {
    const oldFont = loadedFontsFaces.get(postscriptName);
    document.fonts.delete(oldFont);
  }

  try {
    // Try loading the font with different MIME types if it fails
    let fontFace;
    let loaded = false;
    
    // Array of possible MIME types to try
    const mimeTypes = preferredType ? 
      [preferredType] : // Use only the preferred type if specified
      [
        undefined, // Let browser autodetect
        'font/ttf',
        'font/otf', 
        'font/woff',
        'font/woff2'
      ];
    
    // Try each MIME type
    for (const mimeType of mimeTypes) {
      try {
        const fontFormat = mimeType ? { type: mimeType } : undefined;
        
        fontFace = new FontFace(postscriptName, fontData, fontFormat);
        await fontFace.load();
        
        // If we got here, loading succeeded
        loaded = true;
        console.log(`Successfully loaded ${postscriptName}${mimeType ? ` as ${mimeType}` : ''}`);
        break;
      } catch (err) {
        // Continue to next format
      }
    }
    
    if (!loaded) {
      throw new Error(`Could not load font with any available format`);
    }
    
    document.fonts.add(fontFace);
    loadedFontsFaces.set(postscriptName, fontFace);
  } catch (error) {
    console.error(`Failed to load fontface for ${postscriptName}:`, error);
    throw error; // Re-throw the error for handling upstream
  }
};

// Detects font format from buffer - simplified for core functionality
const detectFontFormat = (buffer) => {
  const signature = new Uint8Array(buffer.slice(0, 4));
  const signatureHex = Array.from(signature).map(b => b.toString(16).padStart(2, '0')).join('');
  
  if (signatureHex === '00010000' || signatureHex.startsWith('74727565')) return 'ttf'; // TTF/TrueType
  if (signatureHex === '4f54544f') return 'otf'; // OTF/OpenType
  if (signatureHex === '774f4646') return 'woff'; // WOFF
  if (signatureHex === '774f4632') return 'woff2'; // WOFF2
  
  return 'unknown';
};

// Check if a signed URL is expired or about to expire
export async function checkAndRefreshExpiredUrl(fileURL) {
  if (!fileURL) return false;
  
  // Use the centralized URL checking function
  return await checkAndRefreshExpiredFontUrls();
}

export async function readFontFileData(fileName, fileURL) {
  try {
    // Check and refresh expired URLs before attempting to fetch
    await checkAndRefreshExpiredFontUrls();
    
    // Get the updated font object to ensure we have the latest URL
    const currentFonts = get(detected_fonts);
    const fontObj = currentFonts.find(f => f.file_name === fileName);
    const urlToUse = fontObj?.file_url || fileURL;
    
    // Decode the URL to prevent double-encoding issues
    const decodedURL = decodeURIComponent(urlToUse);
    
    const response = await axios.get(decodedURL, {
      responseType: 'arraybuffer',
      headers: {
        'Accept': '*/*'
      }
    });

    return new File([response.data], fileName, {
      type: 'font/opentype'
    });
  } catch (error) {
    console.error('Failed to fetch font file from S3:', error);
    throw new Error('Font file not available');
  }
}

// -------------------------------------------
// UTILITY functions for processing font files
// -------------------------------------------

// Adds processed font objects to the detected_fonts store if they don't exist
// or merges them to detected fonts if htey already exist
export const addOrMergeFontObjects = (fontObjects) => {
  fontObjects.forEach(font => {
    const existingFont = get(detected_fonts).find(f => f.postscript_name === font.postscript_name);
    if (existingFont) {
      // Create merged font object
      const mergedFont = merge({}, existingFont, font);
      // Update store by replacing the existing font
      detected_fonts.update(fonts =>
        fonts.map(f =>
          f.postscript_name === font.postscript_name ? mergedFont : f
        )
      );
    } else {
      detected_fonts.update(fonts => [...fonts, font]);
    }
  });
  // set detected_fonts to detected_fonts to trigger update
  detected_fonts.set(get(detected_fonts))
};

// Reads a font file and returns its contents as an ArrayBuffer
const readFontFileContents = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = (e) => reject(e);
    reader.readAsArrayBuffer(file);
  });
};

// Extracts and normalizes font metadata from a font object
const extractFontMetadata = (font) => {
  const family = font.names.preferredFamily?.en || font.names.fontFamily?.en || "";
  const subfamily = font.names.preferredSubfamily?.en || font.names.fontSubfamily?.en || "";
  const familyName = String(family)?.replace(/^\s+|\s+$/g, '').replace(/[^\w\s]/g, '').replace(/\s+/g, ' ');

  const parsedStyleVals = getFontStyleAsCssValue(subfamily || font.names.fullName?.en || "");

  return {
    postscriptName: font.names.postScriptName.en || font.names.postScriptName,
    familyName,
    subfamily,
    displayName: font.names.fullName?.en || `${family} ${subfamily}`,
    style: parsedStyleVals.style || '',
    weight: parsedStyleVals.weight || 400,
    stretch: parsedStyleVals.stretch || 'normal'
  };
};

// Creates a font object for uploaded or dropped fonts
const createFontObject = (metadata, fileName, id, source = 'dropped') => {
  const systemFontNames = map(get(system_fonts), f => f.family_name);
  return {
    id: id,
    postscript_name: metadata.postscriptName,
    family_name: metadata.familyName,
    display_name: metadata.displayName,
    file_name: fileName,
    style: metadata.style,
    weight: metadata.weight,
    stretch: metadata.stretch,
    source: source,
    system: systemFontNames.includes(metadata.postscriptName),
    user: !systemFontNames.includes(metadata.postscriptName),
    dropped: source === 'dropped',
    uploaded: source === 'uploaded'
  };
};

// Check if any uploaded font URLs are expired and refresh if needed
export async function checkAndRefreshExpiredFontUrls() {
  const currentFonts = get(detected_fonts);
  const uploadedFonts = currentFonts.filter(f => f.uploaded && f.file_url);
  
  if (uploadedFonts.length === 0) return false;

  // Check the first uploaded font's URL for expiration
  const firstFont = uploadedFonts[0];
  
  try {
    // Parse the URL parameters
    const url = new URL(firstFont.file_url);
    const params = new URLSearchParams(url.search);
    const dateStr = params.get('X-Amz-Date');
    const expiresStr = params.get('X-Amz-Expires');

    if (!dateStr || !expiresStr) return false;

    // Parse the date string (format: YYYYMMDDTHHMMSSZ)
    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(4, 6);
    const day = dateStr.substring(6, 8);
    const hour = dateStr.substring(9, 11);
    const minute = dateStr.substring(11, 13);
    const second = dateStr.substring(13, 15);
    const signedDate = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}Z`);

    // Calculate expiration time
    const expiresSeconds = parseInt(expiresStr);
    const expirationTime = new Date(signedDate.getTime() + (expiresSeconds * 1000));
    const currentTime = new Date();
    const timeUntilExpiration = expirationTime.getTime() - currentTime.getTime();

    // If URL is expired or about to expire within 30 seconds, refresh font URLs
    if (timeUntilExpiration <= 30000) {
      console.log('Font Files - URLs are expired or about to expire, refreshing font URLs');
      await loadUploadedFonts();
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Font Files - Error checking URL expiration:', error);
    return false;
  }
}

// Map uploaded font files to storefront